import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config/api';

interface RelatedKeyword {
  keyword: string;
  volume: number;
  isExisting: boolean;
}

interface RelatedKeywordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  keyword: string;
  onKeywordsAdded: () => void;
}

const RelatedKeywordsModal: React.FC<RelatedKeywordsModalProps> = ({
  isOpen,
  onClose,
  keyword,
  onKeywordsAdded
}) => {
  const [relatedKeywords, setRelatedKeywords] = useState<RelatedKeyword[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingKeywords, setAddingKeywords] = useState<Set<string>>(new Set());
  const [addedKeywords, setAddedKeywords] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<{ total: number; new: number; existing: number } | null>(null);

  useEffect(() => {
    if (isOpen && keyword) {
      fetchRelatedKeywords();
    }
  }, [isOpen, keyword]);

  const fetchRelatedKeywords = async () => {
    setLoading(true);
    setError(null);
    setRelatedKeywords([]);
    setAddedKeywords(new Set());
    
    try {
      const response = await fetch(`${API_URL}/api/keywords/related-hashtags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ keyword }),
      });

      const data = await response.json();
      
      if (data.success) {
        setRelatedKeywords(data.data);
        setStats(data.stats);
      } else {
        setError(data.error || 'Failed to fetch related keywords');
      }
    } catch (err) {
      console.error('Error fetching related keywords:', err);
      setError('Failed to fetch related keywords');
    } finally {
      setLoading(false);
    }
  };

  const formatVolume = (volume: number): string => {
    if (volume >= 1000000000) {
      return `${(volume / 1000000000).toFixed(1)}B`;
    } else if (volume >= 1000000) {
      return `${(volume / 1000000).toFixed(1)}M`;
    } else if (volume >= 1000) {
      return `${(volume / 1000).toFixed(1)}K`;
    }
    return volume.toString();
  };

  const addKeyword = async (keywordToAdd: string) => {
    setAddingKeywords(prev => new Set(prev).add(keywordToAdd));
    
    try {
      const response = await fetch(`${API_URL}/api/keywords`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          keyword: keywordToAdd.toLowerCase().replace(/\s+/g, '') 
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setAddedKeywords(prev => new Set(prev).add(keywordToAdd));
        // Update the isExisting flag in the list
        setRelatedKeywords(prev => 
          prev.map(k => 
            k.keyword === keywordToAdd 
              ? { ...k, isExisting: true } 
              : k
          )
        );
      } else {
        console.error('Failed to add keyword:', data.error);
      }
    } catch (err) {
      console.error('Error adding keyword:', err);
    } finally {
      setAddingKeywords(prev => {
        const newSet = new Set(prev);
        newSet.delete(keywordToAdd);
        return newSet;
      });
    }
  };

  const addAllNewKeywords = async () => {
    const newKeywords = relatedKeywords.filter(k => !k.isExisting && !addedKeywords.has(k.keyword));
    
    if (newKeywords.length === 0) {
      return;
    }

    // Add all keywords to the adding set
    const keywordsToAdd = newKeywords.map(k => k.keyword);
    setAddingKeywords(new Set(keywordsToAdd));

    // Add keywords in batches
    const batchSize = 5;
    for (let i = 0; i < newKeywords.length; i += batchSize) {
      const batch = newKeywords.slice(i, i + batchSize);
      
      await Promise.all(
        batch.map(async (relatedKeyword) => {
          try {
            const response = await fetch(`${API_URL}/api/keywords`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ 
                keyword: relatedKeyword.keyword.toLowerCase().replace(/\s+/g, '') 
              }),
            });

            const data = await response.json();
            
            if (data.success) {
              setAddedKeywords(prev => new Set(prev).add(relatedKeyword.keyword));
              // Update the isExisting flag in the list
              setRelatedKeywords(prev => 
                prev.map(k => 
                  k.keyword === relatedKeyword.keyword 
                    ? { ...k, isExisting: true } 
                    : k
                )
              );
            }
          } catch (err) {
            console.error(`Error adding keyword ${relatedKeyword.keyword}:`, err);
          } finally {
            setAddingKeywords(prev => {
              const newSet = new Set(prev);
              newSet.delete(relatedKeyword.keyword);
              return newSet;
            });
          }
        })
      );
    }

    // Refresh the parent component
    onKeywordsAdded();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{ 
          maxWidth: '800px', 
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div className="modal-header">
          <h2>Related Keywords for "{keyword}"</h2>
          <button onClick={onClose} className="modal-close">✕</button>
        </div>

        <div className="modal-body" style={{ flex: 1, overflow: 'auto' }}>
          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>Finding related keywords...</p>
            </div>
          )}

          {error && (
            <div className="error">
              ❌ {error}
            </div>
          )}

          {!loading && !error && relatedKeywords.length > 0 && (
            <>
              {/* Stats */}
              {stats && (
                <div className="stats-bar" style={{ 
                  marginBottom: '20px',
                  padding: '10px',
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    Found <strong>{stats.total}</strong> related keywords 
                    ({stats.new} new, {stats.existing} existing)
                  </div>
                  {stats.new > 0 && (
                    <button
                      onClick={addAllNewKeywords}
                      disabled={addingKeywords.size > 0}
                      className="btn btn-primary"
                      style={{
                        background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                        color: 'white'
                      }}
                    >
                      {addingKeywords.size > 0 
                        ? `Adding ${addingKeywords.size} keywords...` 
                        : `Add All ${stats.new} New Keywords`}
                    </button>
                  )}
                </div>
              )}

              {/* Keywords table */}
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Keyword</th>
                      <th style={{ width: '30%' }}>Volume</th>
                      <th style={{ width: '30%' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatedKeywords.map((relatedKeyword) => (
                      <tr key={relatedKeyword.keyword}>
                        <td>
                          <span style={{ fontWeight: 500 }}>
                            #{relatedKeyword.keyword}
                          </span>
                        </td>
                        <td>
                          <span style={{ 
                            fontWeight: 'bold',
                            color: relatedKeyword.volume > 10000000 ? '#51cf66' : 
                                   relatedKeyword.volume > 1000000 ? '#ffd43b' : 
                                   '#868e96'
                          }}>
                            {formatVolume(relatedKeyword.volume)}
                          </span>
                        </td>
                        <td>
                          {relatedKeyword.isExisting || addedKeywords.has(relatedKeyword.keyword) ? (
                            <span style={{ 
                              color: '#868e96',
                              fontSize: '14px'
                            }}>
                              ✓ Already Added
                            </span>
                          ) : (
                            <button
                              onClick={() => addKeyword(relatedKeyword.keyword)}
                              disabled={addingKeywords.has(relatedKeyword.keyword)}
                              className="btn btn-secondary"
                              style={{ 
                                padding: '4px 12px',
                                fontSize: '12px'
                              }}
                            >
                              {addingKeywords.has(relatedKeyword.keyword) 
                                ? 'Adding...' 
                                : 'Add Keyword'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!loading && !error && relatedKeywords.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>No related keywords found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default RelatedKeywordsModal;